import axios from "axios";
import { NormalizedTranscript } from "../Investigations/api";
import { AudioType, FileKind, FileSource, LibraryItemLike, Speaker } from "../Menu/uploadFileConfig";

export type FileStatus = "pending" | "uploading" | "processing" | "completed" | "failed";

export interface FilesItem extends LibraryItemLike {
  mediaType: string;
  parentId: string | null;
  parentLabel: string;
  status: FileStatus;
  error?: string;
  createdAt: string;
  usedInCount: number;
  metadata: {
    audioType?: AudioType;
    transcriptionUrl?: string;
    duration?: number;
    context?: string;
    speakers?: Speaker[];
    pageCount?: number;
    description?: string;
    isOther?: boolean;
  };
}

export interface FilesQuery {
  search?: string;
  kind?: FileKind | "";
  source?: FileSource | "";
  audioType?: AudioType | "other" | "";
  sort?: "created_at" | "name" | "kind" | "size";
  dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export const FILE_KIND_LABELS: Record<FileKind, string> = {
  audio: "Audio",
  video: "Video",
  image: "Image",
  document: "Document",
  spreadsheet: "Spreadsheet",
};

export const FILE_SOURCE_LABELS: Record<FileSource, string> = {
  case: "Report",
  form_submission: "Form",
  library: "Saved to Files",
};

export const filesApi = {
  list: (q: FilesQuery) =>
    axios
      .get<{ files: FilesItem[]; meta: { count: number; offset: number; limit: number } }>("/api/files", {
        params: { ...q, kind: q.kind || undefined, source: q.source || undefined, audioType: q.audioType || undefined },
      })
      .then((r) => r.data),
  get: (source: FileSource, id: string) =>
    axios
      .get<{
        file: FilesItem;
        usedIn: { caseId: string; caseNumber: string }[];
        transcribing?: boolean;
        transcribeError?: string;
      }>(`/api/files/${source}/${id}`)
      .then((r) => r.data),
  viewUrl: (source: FileSource, id: string) =>
    axios.get<{ url: string }>(`/api/files/${source}/${id}/view-url`).then((r) => r.data.url),
  transcript: (source: FileSource, id: string) =>
    axios
      .get<{ transcript: NormalizedTranscript | null; participants: { label: string; name?: string; role: string }[] }>(
        `/api/files/${source}/${id}/transcript`,
      )
      .then((r) => r.data),
  text: (source: FileSource, id: string) =>
    axios
      .get<{
        kind: string;
        pages?: { page: number; text: string }[];
        ocrText?: string;
        description?: string;
      }>(`/api/files/${source}/${id}/text`)
      .then((r) => r.data),
  sheets: (source: FileSource, id: string) =>
    axios
      .get<{ sheets: { index: number; name: string; header: string[]; rowCount: number; truncated?: boolean }[] }>(
        `/api/files/${source}/${id}/sheets`,
      )
      .then((r) => r.data.sheets),
  sheetRows: (source: FileSource, id: string, sheet: number, offset: number, limit: number) =>
    axios
      .get<{ header: string[]; rows: string[][]; total: number }>(`/api/files/${source}/${id}/sheets/${sheet}/rows`, {
        params: { offset, limit },
      })
      .then((r) => r.data),
  rename: (id: string, fileName: string) =>
    axios.patch<{ file: FilesItem }>(`/api/files/library/${id}`, { fileName }).then((r) => r.data.file),
  transcribe: (source: FileSource, id: string, audioType?: AudioType) =>
    axios.post(`/api/files/${source}/${id}/transcribe`, { audioType }).then((r) => r.data),
  retry: (id: string) => axios.post(`/api/files/${id}/retry`).then((r) => r.data),
  remove: (source: FileSource, id: string) => axios.delete(`/api/files/${source}/${id}`).then((r) => r.data),
};
